import { PrimeArtifactState } from '../../types/primeModel';
import { PrimeArtifactStateCreate } from '../../types/primeArtifactState';
import { DemandType, DemandFlowType } from '../../types/enums';
import { ArtifactStateTransitionType } from '../../types/enums';
import { Item } from './ItemManager';
import { ItemManager } from './ItemManager';
import { useState, useCallback, useMemo } from 'react';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { StatePropertyManager } from './StatePropertyManager';
import { apiClient } from '../../services/apiClient';
import { Modal, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import styles from './PrimeStateManager.module.css';
import { StateCentricState } from '../../types/primeModelViews';

interface PrimeStateManagerProps {
  prime_artifact_id: string;
  states: PrimeArtifactState[];
  onStateSelect: () => void;
  onStateUpdate: () => void;
  onStateDelete: () => void;
  onStateCreate: () => void;
}

export const PrimeStateManager: React.FC<PrimeStateManagerProps> = ({
  prime_artifact_id,
  states,
  onStateSelect: _onStateSelect,
  onStateUpdate: _onStateUpdate,
  onStateDelete: _onStateDelete,
  onStateCreate: _onStateCreate,
}) => {
  console.log('[PrimeStateManager] Component rendered:', {
    prime_artifact_id,
    statesCount: states?.length || 0,
    states,
  });
  // const dispatch = useDispatch<AppDispatch>();
  // const states = useSelector((state: RootState) => state.primeArtifacts.primeArtifactStates);
  const [selectedState, setSelectedState] = useState<PrimeArtifactState | null>(null);
  const [localState, setLocalState] = useState<PrimeArtifactState | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [stateCentricView, setStateCentricView] = useState<StateCentricState | null>(null);
  const [loadingView, setLoadingView] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [generatingStates, setGeneratingStates] = useState(false);

  // Convert Item to PrimeArtifactState
  const convertToState = useCallback(
    (item: Item): PrimeArtifactState => ({
      id: item.id,
      name: item.name,
      description: item.description || '',
      prime_artifact_ref: prime_artifact_id,
      state_type:
        (item as unknown as PrimeArtifactState).state_type ||
        ArtifactStateTransitionType.OPERATIONAL,
      is_required: (item as unknown as PrimeArtifactState).is_required ?? false,
      is_initial: (item as unknown as PrimeArtifactState).is_initial ?? false,
      is_final: (item as unknown as PrimeArtifactState).is_final ?? false,
      is_improvement: (item as unknown as PrimeArtifactState).is_improvement ?? false,
      is_resource_active: (item as unknown as PrimeArtifactState).is_resource_active ?? false,
      demand_type: (item as unknown as PrimeArtifactState).demand_type || DemandType.TARGET,
      demand_flow_type:
        (item as unknown as PrimeArtifactState).demand_flow_type || DemandFlowType.DRUM,
      state_parameters: (item as unknown as PrimeArtifactState).state_parameters || {},
      validation_rules: (item as unknown as PrimeArtifactState).validation_rules || {},
      state_metadata: (item as unknown as PrimeArtifactState).state_metadata || {},
      state_rules: (item as unknown as PrimeArtifactState).state_rules || {},
      improvement_ref: (item as unknown as PrimeArtifactState).improvement_ref || null,
      improvement_parameters: (item as unknown as PrimeArtifactState).improvement_parameters || {},
      created_at: (item as unknown as PrimeArtifactState).created_at || new Date().toISOString(),
      updated_at: (item as unknown as PrimeArtifactState).updated_at || null,
    }),
    [prime_artifact_id]
  );

  // Memoize the items to prevent unnecessary rerenders
  const items = useMemo(() => {
    console.log('[PrimeStateManager] Computing items from states:', {
      statesCount: states?.length || 0,
      states,
    });
    return states;
  }, [states]);

  // Memoize the selected item to prevent unnecessary rerenders
  const selectedItem = useMemo(() => selectedState, [selectedState]);

  const handleItemSelect = useCallback(
    async (item: Item) => {
      console.log('[StateManager] Item selected:', item);
      const state = states.find((s) => s.id === item.id);
      if (!state) {
        console.error('[StateManager] State not found for item:', item);
        return;
      }
      setSelectedState(state);
      // Fetch state-centric view
      setLoadingView(true);
      setViewError(null);
      try {
        const view = await PrimeArtifactService.getStateCentricView(prime_artifact_id, state.id);
        setStateCentricView(view);
      } catch (err) {
        setViewError(err instanceof Error ? err.message : 'Failed to fetch state-centric view');
        setStateCentricView(null);
      } finally {
        setLoadingView(false);
      }
    },
    [states, prime_artifact_id]
  );

  const handleItemUpdate = useCallback(
    (item: Item) => {
      console.log('[StateManager] Updating state:', item);
      const updatedState = convertToState(item);
      setLocalState(updatedState);
    },
    [convertToState]
  );

  const handleItemDelete = useCallback(
    async (item: Item) => {
      console.log('[StateManager] Deleting state:', item);
      
      // Show confirmation dialog
      const confirmed = window.confirm(
        `Are you sure you want to delete the state "${item.name}"?\n\n` +
        `This will also delete all transitions that use this state as a source or destination.`
      );
      
      if (!confirmed) {
        return;
      }
      
      try {
        await PrimeArtifactService.deletePrimeArtifactState(prime_artifact_id, item.id);
        // Call the callback to refresh states from the parent
        _onStateDelete();
      } catch (error) {
        console.error('[StateManager] Failed to delete state:', error);
        alert(error instanceof Error ? error.message : 'Failed to delete state');
      }
    },
    [prime_artifact_id, _onStateDelete]
  );

  const handleStateCreate = useCallback(() => {
    console.log('[StateManager] Creating new state');
    const newState: Omit<PrimeArtifactState, 'id'> = {
      name: '',
      description: '',
      prime_artifact_ref: prime_artifact_id,
      state_type: ArtifactStateTransitionType.OPERATIONAL,
      is_required: false,
      is_initial: false,
      is_final: false,
      is_improvement: false,
      is_resource_active: false,
      demand_type: DemandType.SUPPLY,
      demand_flow_type: DemandFlowType.PULL,
      state_parameters: {},
      validation_rules: {},
      state_metadata: {},
      state_rules: {},
      improvement_ref: null,
      improvement_parameters: {},
      created_at: new Date().toISOString(),
      updated_at: null,
    };
    console.log('[StateManager] New state:', newState);
    setLocalState(newState as PrimeArtifactState);
    setIsEditing(true);
  }, [prime_artifact_id]);

  const handleStateSave = useCallback(async () => {
    if (!localState) {
      console.error('[StateManager] Cannot save: localState is null');
      return;
    }

    try {
      console.log('[StateManager] Saving state:', localState);

      // Create a new object without the id field for new state creation
      const stateToSend = localState.id
        ? localState
        : ({
            ...localState,
            id: undefined,
          } as Omit<PrimeArtifactState, 'id'>);

      // Ensure all required fields are present and properly formatted
      const stateData = {
        ...stateToSend,
        name: stateToSend.name || '',
        description: stateToSend.description || '',
        prime_artifact_ref: prime_artifact_id,
        state_type: stateToSend.state_type,
        is_required: stateToSend.is_required ?? false,
        is_initial: stateToSend.is_initial ?? false,
        is_final: stateToSend.is_final ?? false,
        is_improvement: stateToSend.is_improvement ?? false,
        demand_type: stateToSend.demand_type,
        demand_flow_type: stateToSend.demand_flow_type,
        state_parameters: stateToSend.state_parameters || {},
        validation_rules: stateToSend.validation_rules || {},
        state_metadata: stateToSend.state_metadata || {},
        state_rules: stateToSend.state_rules || {},
        improvement_ref: stateToSend.improvement_ref || null,
        improvement_parameters: stateToSend.improvement_parameters || {},
      };

      console.log('[StateManager] Sending state data to server:', stateData);

      if (localState.id) {
        // Update existing state
        await PrimeArtifactService.updatePrimeArtifactState(localState.id, stateData);
        console.log('[StateManager] State updated successfully');
      } else {
        // Create new state
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...stateWithoutId } = stateData as unknown as {
          id: string;
          [key: string]: unknown;
        };
        await PrimeArtifactService.createPrimeArtifactState(
          stateWithoutId as unknown as PrimeArtifactStateCreate
        );
        console.log('[StateManager] State created successfully');
      }

      // Refresh the states list
      setIsEditing(false);
      setLocalState(null);
      
      // Call the callback to refresh states from the parent
      if (localState.id) {
        _onStateUpdate();
      } else {
        _onStateCreate();
      }
    } catch (error) {
      console.error('[StateManager] Failed to save state:', error);
      alert(error instanceof Error ? error.message : 'Failed to save state');
    }
  }, [prime_artifact_id, localState, _onStateUpdate, _onStateCreate]);

  const handleEdit = useCallback(() => {
    if (selectedState) {
      setLocalState(selectedState);
      setIsEditing(true);
    }
  }, [selectedState]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setLocalState(selectedState);
  }, [selectedState]);

  const handleResetToTemplate = useCallback(async () => {
    Modal.confirm({
      title: 'Reset to Template',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>This will DELETE all existing states and transitions for this prime artifact and replace them with the default template from the artifact type.</p>
          <p><strong>This action cannot be undone.</strong></p>
          <p>Are you sure you want to continue?</p>
        </div>
      ),
      okText: 'Reset to Template',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await apiClient.post(`/prime-artifacts/${prime_artifact_id}/apply-template`);
          message.success(
            `Template applied successfully! Created ${response.data.states_created} states and ${response.data.transitions_created} transitions.`
          );
          // Refresh the states list
          _onStateUpdate();
        } catch (error: any) {
          console.error('Error applying template:', error);
          message.error(error.response?.data?.detail || 'Failed to apply template');
        }
      },
    });
  }, [prime_artifact_id, _onStateUpdate]);

  // Generate states from properties state_actions
  const handleGenerateStatesFromProperties = useCallback(async () => {
    Modal.confirm({
      title: 'Generate States from Properties',
      icon: <ExclamationCircleOutlined style={{ color: '#1890ff' }} />,
      content: (
        <div>
          <p>This will analyze the property <code>state_actions</code> definitions and generate states based on them.</p>
          <p>Existing states with the same names will be skipped.</p>
          <p>Do you want to proceed?</p>
        </div>
      ),
      okText: 'Generate States',
      cancelText: 'Cancel',
      onOk: async () => {
        setGeneratingStates(true);
        try {
          const response = await apiClient.post(`/prime-artifacts/${prime_artifact_id}/generate/states`);
          const result = response.data;
          
          if (result.states_created > 0) {
            message.success(
              `Generated ${result.states_created} states from property definitions.${result.states_skipped > 0 ? ` (${result.states_skipped} skipped - already existed)` : ''}`
            );
          } else if (result.states_skipped > 0) {
            message.info(`All ${result.states_skipped} states already exist.`);
          } else {
            message.warning('No states could be derived from property state_actions. Make sure properties have state_actions defined.');
          }
          
          // Refresh the states list
          _onStateUpdate();
        } catch (error: any) {
          console.error('Error generating states:', error);
          message.error(error.response?.data?.detail || 'Failed to generate states from properties');
        } finally {
          setGeneratingStates(false);
        }
      },
    });
  }, [prime_artifact_id, _onStateUpdate]);

  // State for showing advanced options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const renderStateDetails = useCallback(
    (item: Item, isEditing: boolean) => {
      // Use localState if we're editing, otherwise use the item
      const stateToRender = isEditing && localState ? localState : convertToState(item);

      return (
        <div className={styles.stateDetailsCompact}>
          {/* Essential Fields */}
          <div className={styles.field}>
            <label>Name:</label>
            <input
              type="text"
              value={stateToRender.name || ''}
              onChange={(e) => handleItemUpdate({ ...stateToRender, name: e.target.value })}
              disabled={!isEditing}
            />
          </div>
          <div className={styles.field}>
            <label>Description:</label>
            <textarea
              value={stateToRender.description || ''}
              onChange={(e) => handleItemUpdate({ ...stateToRender, description: e.target.value })}
              disabled={!isEditing}
            />
          </div>
          
          <div className={styles.stateFormColumns}>
            {/* Simplified main fields */}
            <div className={`${styles.formCol} ${styles.enums}`}>
              <div className={styles.field}>
                <label>State Type:</label>
                <select
                  value={stateToRender.state_type || ArtifactStateTransitionType.OPERATIONAL}
                  onChange={(e) =>
                    handleItemUpdate({
                      ...stateToRender,
                      state_type: e.target.value as ArtifactStateTransitionType,
                    })
                  }
                  disabled={!isEditing}
                >
                  <option value={ArtifactStateTransitionType.OPERATIONAL}>Operational</option>
                  <option value={ArtifactStateTransitionType.IMPROVEMENT}>Improvement</option>
                  <option value={ArtifactStateTransitionType.CREATION}>Creation</option>
                </select>
              </div>
            </div>
            <div className={`${styles.formCol} ${styles.checkboxes}`}>
              <div className={`${styles.field} ${styles.checkbox}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={stateToRender.is_initial ?? false}
                    onChange={(e) =>
                      handleItemUpdate({ ...stateToRender, is_initial: e.target.checked })
                    }
                    disabled={!isEditing}
                  />
                  Initial State
                </label>
              </div>
              <div className={`${styles.field} ${styles.checkbox}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={stateToRender.is_final ?? false}
                    onChange={(e) =>
                      handleItemUpdate({ ...stateToRender, is_final: e.target.checked })
                    }
                    disabled={!isEditing}
                  />
                  Final State
                </label>
              </div>
            </div>
          </div>

          {/* Advanced Options - Hidden by default */}
          <details className={styles.advancedSection} open={showAdvancedOptions}>
            <summary 
              onClick={(e) => {
                e.preventDefault();
                setShowAdvancedOptions(!showAdvancedOptions);
              }}
              className={styles.advancedSummary}
            >
              Advanced Options
            </summary>
            <div className={styles.advancedContent}>
              <div className={styles.stateFormColumns}>
                <div className={`${styles.formCol} ${styles.enums}`}>
                  <div className={styles.field}>
                    <label>Demand Type:</label>
                    <select
                      value={stateToRender.demand_type || DemandType.SUPPLY}
                      onChange={(e) =>
                        handleItemUpdate({
                          ...stateToRender,
                          demand_type: e.target.value as DemandType,
                        })
                      }
                      disabled={!isEditing}
                    >
                      <option value={DemandType.SUPPLY}>Supply (Default)</option>
                      <option value={DemandType.TARGET}>Target</option>
                      <option value={DemandType.CAPACITY}>Capacity</option>
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label>Demand Flow Type:</label>
                    <select
                      value={stateToRender.demand_flow_type || DemandFlowType.PULL}
                      onChange={(e) =>
                        handleItemUpdate({
                          ...stateToRender,
                          demand_flow_type: e.target.value as DemandFlowType,
                        })
                      }
                      disabled={!isEditing}
                    >
                      <option value={DemandFlowType.PULL}>Pull (Default)</option>
                      <option value={DemandFlowType.DRUM}>Drum</option>
                      <option value={DemandFlowType.PUSH}>Push</option>
                    </select>
                  </div>
                </div>
                <div className={`${styles.formCol} ${styles.checkboxes}`}>
                  <div className={`${styles.field} ${styles.checkbox}`}>
                    <label>
                      <input
                        type="checkbox"
                        checked={stateToRender.is_required ?? false}
                        onChange={(e) =>
                          handleItemUpdate({ ...stateToRender, is_required: e.target.checked })
                        }
                        disabled={!isEditing}
                      />
                      Required
                    </label>
                  </div>
                  <div className={`${styles.field} ${styles.checkbox}`}>
                    <label>
                      <input
                        type="checkbox"
                        checked={stateToRender.is_improvement ?? false}
                        onChange={(e) =>
                          handleItemUpdate({ ...stateToRender, is_improvement: e.target.checked })
                        }
                        disabled={!isEditing}
                      />
                      Improvement
                    </label>
                  </div>
                  <div className={`${styles.field} ${styles.checkbox}`}>
                    <label>
                      <input
                        type="checkbox"
                        checked={stateToRender.is_resource_active ?? false}
                        onChange={(e) =>
                          handleItemUpdate({ ...stateToRender, is_resource_active: e.target.checked })
                        }
                        disabled={!isEditing}
                      />
                      Resource Active
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </details>

          {/* State-centric view section */}
          {loadingView && <div>Loading state-centric view...</div>}
          {viewError && <div className={styles.error}>{viewError}</div>}
          {stateCentricView && (
            <div className={styles.stateCentricSection}>
              <div className={styles.stateCentricColumns}>
                <div className={styles.stateCentricCol}>
                  <h4>Incoming States</h4>
                  {stateCentricView.incoming_transitions.length === 0 ? (
                    <div className={styles.empty}>No incoming states</div>
                  ) : (
                    <ul>
                      {stateCentricView.incoming_transitions.map((tr) => (
                        <li key={tr.id}>
                          <strong>{tr.from_state_name}</strong> via <em>{tr.process_name}</em> (
                          <span>{tr.default_transition_type}</span>)
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className={styles.stateCentricCol}>
                  <h4>Outgoing States</h4>
                  {stateCentricView.outgoing_transitions.length === 0 ? (
                    <div className={styles.empty}>No outgoing states</div>
                  ) : (
                    <ul>
                      {stateCentricView.outgoing_transitions.map((tr) => (
                        <li key={tr.id}>
                          <strong>{tr.to_state_name}</strong> via <em>{tr.process_name}</em> (
                          <span>{tr.default_transition_type}</span>)
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* State Properties Section */}
          {!isEditing && stateToRender.id && (
            <div className={styles.statePropertiesSection}>
              <StatePropertyManager
                stateId={stateToRender.id}
                stateName={stateToRender.name}
                onPropertyUpdated={() => {
                  // Optionally refresh state data or trigger parent update
                  console.log('Properties updated for state:', stateToRender.name);
                }}
              />
            </div>
          )}
        </div>
      );
    },
    [handleItemUpdate, localState, convertToState, loadingView, viewError, stateCentricView]
  );

  return (
    <div className={styles.stateManager}>
      <ItemManager
        title="States"
        items={items as unknown as Item[]}
        selectedItem={selectedItem as Item | null}
        onSelect={handleItemSelect}
        onUpdate={handleItemUpdate}
        onDelete={handleItemDelete}
        onAdd={handleStateCreate}
        onSave={handleStateSave}
        onEdit={handleEdit}
        onCancel={handleCancel}
        isEditing={isEditing}
        renderDetails={renderStateDetails}
        additionalActions={
          <>
            <button 
              onClick={handleGenerateStatesFromProperties}
              disabled={generatingStates}
              style={{ 
                marginRight: '8px', 
                backgroundColor: '#1890ff', 
                color: 'white', 
                border: 'none', 
                padding: '4px 8px', 
                borderRadius: '4px',
                cursor: generatingStates ? 'wait' : 'pointer',
                opacity: generatingStates ? 0.7 : 1
              }}
            >
              {generatingStates ? 'Generating...' : 'Generate from Properties'}
            </button>
            <button 
              onClick={handleResetToTemplate} 
              className="reset-template-button"
              style={{ 
                marginRight: '8px', 
                backgroundColor: '#ff4d4f', 
                color: 'white', 
                border: 'none', 
                padding: '4px 8px', 
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Reset to Template
            </button>
          </>
        }
      />
    </div>
  );
};
